import {Component, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {ControlValueAccessor, FormBuilder, FormControl, NG_VALUE_ACCESSOR} from "@angular/forms";
import {Ofwca} from "../../interface/ofwca";
import {ReceivablesBalance} from "../../interface/receivables-balance";
import {ReplaySubject} from "rxjs";
import {ListResponse} from "../../interface/response/list-response";
import {OfwcaService} from "../../services/ofwca.service";
import {ReceivablesBalanceService} from "../../services/receivables-balance.service";
import {debounceTime, distinctUntilChanged, startWith, takeUntil} from "rxjs/operators";
import {MatSelectChange} from "@angular/material/select";

@Component({
  selector: 'app-dict-receivables-balance',
  templateUrl: './dict-receivables-balance.component.html',
  styleUrls: ['./dict-receivables-balance.component.scss'],
  providers: [
    {provide: NG_VALUE_ACCESSOR, multi: true, useExisting: forwardRef(() => DictReceivablesBalanceComponent)}
  ]
})
export class DictReceivablesBalanceComponent implements OnInit, OnDestroy, ControlValueAccessor {

  @Input() label: string;
  @Input() multiple = false;
  @Input() control: FormControl = null;
  @Input() required = false;
  @Input() receivablesBalanceItems: ReceivablesBalance[] = null;
  @Output() selected: EventEmitter<ReceivablesBalance> = new EventEmitter<ReceivablesBalance>();

  externalControl = true;
  items: ReceivablesBalance[] = [];
  disabled = false;
  value?: string = null;
  receivablesBalanceFilterCtrl: FormControl;
  itemsSubject: ReplaySubject<ListResponse<ReceivablesBalance>> = new ReplaySubject<ListResponse<ReceivablesBalance>>(1);

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private receivablesBalanceService: ReceivablesBalanceService,
    private fb: FormBuilder,
  ) {
    this.receivablesBalanceFilterCtrl = fb.control('');
  }

  onChange: any = () => { };
  onTouched: any = () => { };

  ngOnInit(): void {
    if ( this.receivablesBalanceItems !== null) {
      this.items = this.receivablesBalanceItems;
    }
    if (this.control === null) {
      this.control = new FormControl('');
      this.externalControl = false;
    }

    this.control.valueChanges
      .pipe(
        startWith(this.control.value),
        takeUntil(this.destroyed$)
      )
      .subscribe((val: string) => {
        if (val) {
          let exists = false;
          this.items.forEach((receivablesBalance: ReceivablesBalance) => {
            if (receivablesBalance.uuid === val) {
              exists = true;
            }
          });

          if (!exists) {
            this.receivablesBalanceService.loadNotCountedList(0, 9999, {
              in$uuid: val
            }, this.itemsSubject);
          }
        }
      });

    this.itemsSubject
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result: ListResponse<ReceivablesBalance>) => {
        if ( this.receivablesBalanceItems === null) {
          this.items = result.items;
        } else{
          this.items = this.receivablesBalanceItems;
        }
      });

    this.receivablesBalanceFilterCtrl.valueChanges
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        takeUntil(this.destroyed$)
      )
      .subscribe((val) => {
        if (val) {
          this.receivablesBalanceService.loadNotCountedList(0, 5, {
            context: val,
          }, this.itemsSubject);
        } else if (this.control.value) {
          this.receivablesBalanceService.loadNotCountedList(0, 9999, {
            in$uuid: this.control.value
          }, this.itemsSubject);
        }
      });
  }
  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }


  setDisabledState(isDisabled: boolean): void {
    if (this.externalControl) {
      return;
    }

    if (isDisabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }

    this.disabled = isDisabled;
  }

  writeValue(value: any): void {
    if (this.externalControl) {
      return;
    }

    this.control.setValue(value);
    this.value = value;
  }

  onSelectionChange(e: MatSelectChange): void {
    this.items.forEach((receivablesBalance: ReceivablesBalance) => {
      if (receivablesBalance.uuid === e.value) {
        this.selected.emit(receivablesBalance);
      }
    });

    this.value = e.value;
    this.onChange(this.value);
  }

  clearValue(): void {
    this.control.setValue(this.multiple ? [] : '');
    this.writeValue(this.multiple ? [] : '');
    this.onChange(this.value);
    this.selected.emit();
  }
}
